// ============================================================
// HABIT OS — Einwilligung vor dem KI-Begleiter
// Zeigt die Kinder (AgentChat) erst nach gültiger Einwilligung.
// Vorher wird nichts an die KI gesendet (auch keine Begrüßung).
// ============================================================

import React, { useState, useEffect } from "react";
import { useConsent, CHAT_DELETED_EVENT } from "../hooks/useConsent";
import { CONSENT_TITLE, CONSENT_SECTIONS, CONSENT_CHECKBOX } from "../data/consent";

export function ConsentGate({ userId, Z, children }) {
  const { hasConsent, loading, grant } = useConsent(userId);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [declined, setDeclined] = useState(false);
  const [chatKey, setChatKey] = useState(0);

  // Nach "Chatverlauf löschen" im Profil den Chat neu aufbauen (lädt dann leer)
  useEffect(() => {
    const onDeleted = () => setChatKey(k => k + 1);
    window.addEventListener(CHAT_DELETED_EVENT, onDeleted);
    return () => window.removeEventListener(CHAT_DELETED_EVENT, onDeleted);
  }, []);

  if (loading) return null;
  if (hasConsent) return <React.Fragment key={chatKey}>{children}</React.Fragment>;

  const font = "'Plus Jakarta Sans', sans-serif";
  const p = { fontSize: 13, color: Z.onSurfaceVariant, lineHeight: 1.6, margin: "0 0 4px", fontFamily: font };

  const handleGrant = async () => {
    setSaving(true);
    setError(null);
    try {
      await grant();
    } catch {
      setError("Speichern hat nicht geklappt. Bitte versuch es noch einmal.");
    } finally {
      setSaving(false);
    }
  };

  if (declined) {
    return (
      <div style={{ padding: "32px 24px", maxWidth: 480, margin: "0 auto", fontFamily: font, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
        <p style={{ ...p, fontSize: 14 }}>Der KI-Begleiter bleibt aus. Alles andere in Habit OS funktioniert wie gewohnt, und es werden keine Daten an die KI gesendet.</p>
        <button onClick={() => setDeclined(false)} style={{ marginTop: 16, padding: "10px 18px", borderRadius: 20, border: `1px solid ${Z.outlineVariant}`, background: Z.surfaceContainerLowest, color: Z.onSurface, fontSize: 13, cursor: "pointer", fontFamily: font }}>
          Einwilligung ansehen
        </button>
      </div>
    );
  }

  return (
    <div style={{ overflowY: "auto", height: "100%", WebkitOverflowScrolling: "touch" }}>
      <div style={{ padding: "24px 20px 32px", maxWidth: 480, margin: "0 auto", fontFamily: font }}>
        <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 20, fontWeight: 500, color: Z.onSurface, margin: "0 0 16px", lineHeight: 1.3 }}>{CONSENT_TITLE}</h2>

        {CONSENT_SECTIONS.map(s => (
          <div key={s.heading} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: Z.onSurface, marginBottom: 2 }}>{s.heading}</div>
            <p style={p}>{s.text}</p>
          </div>
        ))}

        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", borderRadius: 14, border: `1px solid ${checked ? Z.primary : Z.outlineVariant}`, background: Z.surfaceContainerLowest, cursor: "pointer", marginTop: 8 }}>
          <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18, accentColor: Z.primary, flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: Z.onSurface, lineHeight: 1.5 }}>{CONSENT_CHECKBOX}</span>
        </label>

        {error && <p style={{ ...p, color: Z.error, marginTop: 8 }}>{error}</p>}

        <button
          onClick={handleGrant}
          disabled={!checked || saving}
          style={{ width: "100%", marginTop: 16, padding: 14, borderRadius: 14, border: "none", background: checked ? Z.primary : Z.outlineVariant, color: Z.onPrimary, fontSize: 15, fontWeight: 600, cursor: checked && !saving ? "pointer" : "default", fontFamily: font }}
        >
          {saving ? "Wird gespeichert…" : "Einwilligen und Begleiter starten"}
        </button>
        <button
          onClick={() => setDeclined(true)}
          style={{ width: "100%", marginTop: 8, padding: 12, borderRadius: 14, border: "none", background: "transparent", color: Z.onSurfaceVariant, fontSize: 14, cursor: "pointer", fontFamily: font }}
        >
          Ohne KI-Begleiter weiter
        </button>
      </div>
    </div>
  );
}
